/**
 * PAYMENT SECTION
 * Section "Paiement" réutilisable, intégrée dans ContactDetailPage et ProspectDetailPanel
 * Affiche les payment_cases BPI d'un contact avec badge statut, grille infos, bouton confirmation
 */

import { useMemo, useState } from 'react';
import { BadgeCheck, Copy, CreditCard, LinkIcon, Mail, RefreshCw, Undo2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from '../ui';
import { usePaymentCase, usePaymentCasesByCrew } from '../../hooks/usePaymentCase';
import { buildBedPaymentLink, useBedActions } from '../../hooks/useBedActions';
import {
  useBpiManualValidationActions,
  useBpiManualValidations,
} from '../../hooks/useBpiManualValidation';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import type { PaymentCaseWithRelations } from '../../hooks/usePaymentCase';
import { ConfirmPaymentDialog } from './ConfirmPaymentDialog';
import { ManualBpiValidationDialog } from './ManualBpiValidationDialog';
import { RestoreExpiredDialog } from './RestoreExpiredDialog';
import { RecordRefundDialog } from './RecordRefundDialog';
import { BedLinkDialog } from './BedLinkDialog';
import { ResendBpiLinkDialog } from './ResendBpiLinkDialog';
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_SCOPE_LABELS,
  PAYMENT_METHOD,
  PAYMENT_SCOPE,
  PAYMENT_STATUS,
} from '../../types/payment';
import type { PaymentAttempt } from '../../types/payment';

// Statuts terminaux d'un dossier BED : un nouveau lien ne doit pas être émis
// par-dessus (la RPC 00130 lève bed_case_already_terminal).
const BED_TERMINAL_STATUSES = new Set<string>([
  PAYMENT_STATUS.PAID,
  PAYMENT_STATUS.EXPIRED,
  PAYMENT_STATUS.FAILED,
]);

// Statuts d'un dossier BPI pour lesquels le mail de paiement peut être renvoyé
// (TICKET-CRM-10) : tant que le participant n'a pas payé et que le dossier
// n'est pas terminal. La route 4LT choisit le template selon le statut.
const RESENDABLE_BPI_STATUSES = new Set<string>([
  PAYMENT_STATUS.DRAFT,
  PAYMENT_STATUS.PENDING_CARD,
  PAYMENT_STATUS.PENDING_TRANSFER,
  PAYMENT_STATUS.PENDING_CHEQUE,
]);

// Somme des tentatives effectivement encaissées (succeeded===true). Sert au
// calcul du « reste à payer » BED multi-CB.
function sumSucceededCents(attempts: PaymentAttempt[]): number {
  return attempts.reduce((acc, a) => (a.succeeded ? acc + a.amount_cents : acc), 0);
}

const MANUAL_CARD_CONFIRMATION_EMAILS = new Set([
  'service.administratif@desertours.com',
  'florence@desertours.com',
]);

// Ce qu'il faut au BedLinkDialog pour calculer le solde et émettre le lien.
// `paidBpiCaseId` est null quand le BPI a été validé MANUELLEMENT (T1) : la
// preview passe alors par le porteur (get_bed_solde_for_owner) et il n'y a
// aucun dossier d'où tirer les payeurs candidats.
interface BedDialogSource {
  paidBpiCaseId: string | null;
  productId: string;
  productName: string;
}

type PaymentSectionProps =
  | {
      contactId: string;
      crewId?: never;
      contactName: string;
      compact?: boolean;
      onPaymentConfirmed?: () => void;
      // Le pont crew_members (00242) n'a de sens que pour un porteur équipage.
      crewMembers?: never;
      // Un porteur contact peut couvrir plusieurs éditions : l'édition est
      // déduite de ses dossiers BPI quand elle est unique (cf. corps).
      productId?: never;
    }
  | {
      crewId: string;
      contactId?: never;
      contactName: string;
      compact?: boolean;
      onPaymentConfirmed?: () => void;
      /**
       * (00242) Membres de l'équipage. Une attestation « BPI réglé » posée sur
       * un MEMBRE (typiquement le pilote, avant que l'équipage n'existe)
       * débloque le BED de l'équipage côté serveur — le pont `crew_members` de
       * `resolve_bpi_manual_proof`. Sans cette liste, la fiche équipage
       * n'affiche pas la preuve qui la débloque et le staff en repose une
       * seconde. Les noms servent à indiquer la PROVENANCE de l'attestation.
       */
      crewMembers?: PaymentSectionCrewMember[];
      /**
       * (00242) Édition de l'équipage (`crews.product_id`). Une attestation
       * « BPI réglé » porte toujours sur un produit précis : sans ce filtre, la
       * fiche affichait celles des AUTRES éditions du porteur — et, via le pont
       * `crewMembers`, celles des autres éditions de ses membres (un pilote 4L
       * 2026 + 2027). Montant faux à l'écran, et « Retirer » qui visait la
       * mauvaise ligne.
       */
      productId?: string | null;
    };

/** Membre d'équipage, réduit à ce dont cette section a besoin. */
export interface PaymentSectionCrewMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// Date seule (YYYY-MM-DD, ex. date d'un remboursement) : lue à midi heure
// locale, sinon `new Date('2026-11-12')` (minuit UTC) s'affiche la veille
// dans un fuseau à l'ouest de Greenwich.
function formatDayOnly(isoDay: string): string {
  return formatDate(`${isoDay}T12:00:00`);
}

function formatAmount(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(amountCents / 100);
}

function PaymentCaseCard({
  paymentCase,
  contactName,
  compact,
  onConfirmed,
  onBedLinkRegenerated,
  onRefundRecorded,
}: {
  paymentCase: PaymentCaseWithRelations;
  contactName: string;
  compact?: boolean;
  onConfirmed: () => void;
  // TICKET-BED-15 : refresh seul (PAS onConfirmed — réémettre un lien n'est
  // pas une confirmation de paiement, onPaymentConfirmed ne doit pas partir).
  onBedLinkRegenerated: () => void | Promise<void>;
  // 00295 : refresh seul, noter un remboursement n'est pas un paiement.
  onRefundRecorded: () => void | Promise<void>;
}) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const { user } = useAuth();
  const { canManageAssignments } = usePermissions();
  const { generateBedLink } = useBedActions();

  const isBed = paymentCase.scope === PAYMENT_SCOPE.BED;
  const statusColors = PAYMENT_STATUS_COLORS[paymentCase.status];
  // Pour un BED draft (lien émis, en attente du 1er CB), « Lien envoyé » est
  // plus parlant que « En attente de choix ».
  const statusLabel =
    isBed && paymentCase.status === PAYMENT_STATUS.DRAFT
      ? 'Lien envoyé'
      : PAYMENT_STATUS_LABELS[paymentCase.status];
  const scopeLabel = PAYMENT_SCOPE_LABELS[paymentCase.scope];
  const userEmail = user?.email?.toLowerCase() ?? '';
  const canConfirmManualCardForUser = MANUAL_CARD_CONFIRMATION_EMAILS.has(userEmail);
  // BED multi-CB : reste à payer = montant - somme des tentatives encaissées.
  const succeededCents = isBed ? sumSucceededCents(paymentCase.payment_attempts) : 0;
  const remainingCents = isBed ? Math.max(0, paymentCase.amount_cents - succeededCents) : 0;
  // 00295 : trop-perçu = encaissé au-delà du montant du dossier ; il reste à
  // rembourser tant que les remboursements notés ne le couvrent pas.
  const overpaidCents = isBed ? Math.max(0, succeededCents - paymentCase.amount_cents) : 0;
  const refunds = isBed
    ? paymentCase.payment_refunds
        .slice()
        .sort((a, b) =>
          a.refunded_on === b.refunded_on
            ? a.created_at.localeCompare(b.created_at)
            : a.refunded_on.localeCompare(b.refunded_on)
        )
    : [];
  const refundedCents = refunds.reduce((acc, r) => acc + r.amount_cents, 0);
  const toRefundCents = Math.max(0, overpaidCents - refundedCents);
  // Un remboursement ne se note que sur un dossier soldé (record_payment_refund
  // refuse sinon) : un dossier sur-couvert encore en attente se clôt d'abord.
  const canRecordRefund = toRefundCents > 0 && paymentCase.status === PAYMENT_STATUS.PAID;
  // Confirmation manuelle offline (virement/chèque) : BPI et BED (TICKET-BED-16).
  // Pour un BED, la RPC confirm_payment_with_timeline trace un payment_attempt
  // succeeded du montant reçu (00294 : partiel possible ; 00295 : au-delà du
  // restant dû, trop-perçu) et
  // ne consomme les bed_tokens qu'au solde. Un BED `partial` (versement déjà
  // reçu) accepte un nouveau virement ou chèque s'il reste un montant dû
  // (TICKET-BED-25) ; couvert mais pas encore soldé, le reconcile le soldera.
  const canConfirmOffline =
    paymentCase.status === PAYMENT_STATUS.PENDING_TRANSFER ||
    paymentCase.status === PAYMENT_STATUS.PENDING_CHEQUE ||
    (isBed && paymentCase.status === PAYMENT_STATUS.PARTIAL && remainingCents > 0);
  const canConfirmManualCard =
    canConfirmManualCardForUser &&
    paymentCase.scope === PAYMENT_SCOPE.BPI &&
    paymentCase.status === PAYMENT_STATUS.PENDING_CARD &&
    paymentCase.method === PAYMENT_METHOD.CARD;
  const canConfirm = canConfirmOffline || canConfirmManualCard;

  // Sous-commandes (fractions CB) : provider_order_id de type {order_id}-NN.
  // + versements virement/chèque enregistrés par le staff (BED-25).
  const subOrders = isBed
    ? paymentCase.payment_attempts
        .filter((a) => a.method === PAYMENT_METHOD.CARD || a.succeeded)
        .slice()
        .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
    : [];

  // DATA-02 : ré-ouverture d'un dossier auto-expiré, réservée admin/superadmin
  // (la RPC restore_expired_payment_case re-vérifie le rôle côté serveur).
  const canRestore = canManageAssignments() && paymentCase.status === PAYMENT_STATUS.EXPIRED;

  // Lien de paiement BED actif : token non consommé et non expiré le plus
  // récent. Ré-affichable/copiable à tout moment tant que le dossier n'est pas
  // terminal (le lien généré dans BedLinkDialog n'était sinon plus visible
  // après fermeture du dialog).
  const activeBedToken =
    isBed && !BED_TERMINAL_STATUSES.has(paymentCase.status)
      ? (paymentCase.bed_tokens
          .filter((t) => t.used_at === null && new Date(t.expires_at) > new Date())
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null)
      : null;
  // code produit passé pour router la base du tunnel PAR PRODUIT (Lot 2 BED,
  // GAP-2 ; review Victor #3 : products.code, plus le préfixe de l'order_id) ;
  // défaut = tunnel partagé si le produit n'a pas de tunnel dédié.
  const activeBedLink = activeBedToken
    ? buildBedPaymentLink(activeBedToken.token, paymentCase.product_code)
    : null;

  async function handleCopyBedLink() {
    if (!activeBedLink) return;
    try {
      await navigator.clipboard.writeText(activeBedLink);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier le lien.');
    }
  }

  // TICKET-BED-15 : un BED non-terminal sans token actif (expiré, ou révoqué
  // par une réémission 00136) était une impasse — l'Edge send-bed-link répond
  // 410 no_active_token et le bouton global « Générer un lien BED » est masqué
  // dès qu'un BED existe pour le produit. La RPC create_bed_payment_case est
  // idempotente : rappelée sur ce dossier, elle réémet un token et révoque les
  // anciens. Montant/payeur du dossier repassés tels quels (pas de mutation).
  const canRegenerateBedLink =
    isBed && !BED_TERMINAL_STATUSES.has(paymentCase.status) && !activeBedToken;

  async function handleRegenerateBedLink() {
    setRegenerating(true);
    try {
      const result = await generateBedLink({
        crewId: paymentCase.crew_id ?? undefined,
        contactId: paymentCase.contact_id ?? undefined,
        amountCents: paymentCase.amount_cents,
        payerContactId: paymentCase.payer_contact_id ?? undefined,
      });
      if (!result.success) {
        toast.error(result.error ?? 'Erreur lors de la régénération du lien BED.');
        return;
      }
      toast.success('Nouveau lien généré — les anciens liens de ce dossier sont révoqués.');
      // Refresh seul : le nouveau token apparaît (bloc lien actif + Copier).
      await onBedLinkRegenerated();
    } finally {
      setRegenerating(false);
    }
  }

  // Trouver la dernière tentative payée (pour CB) afin d'avoir le provider_transaction_id
  const paidAttempt = paymentCase.payment_attempts.find(
    (a) => a.provider_transaction_id && a.method === 'card' && a.provider_status === 'PAID'
  );

  const methodLabel = paymentCase.method ? PAYMENT_METHOD_LABELS[paymentCase.method] || '—' : '—';

  const confirmMethodLabel = canConfirmManualCard
    ? 'CB ancienne plateforme'
    : paymentCase.status === PAYMENT_STATUS.PENDING_TRANSFER
      ? 'virement'
      : 'chèque';

  return (
    <>
      <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4">
        {/* Header : nom produit + badge statut */}
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-zinc-900">
            {scopeLabel} — {paymentCase.product_name || 'Produit inconnu'}
          </span>
          <Badge
            className={`${statusColors.bg} ${statusColors.text} border ${statusColors.border}`}
          >
            {statusLabel}
          </Badge>
        </div>

        {/* Grille 2 colonnes */}
        <div className={`grid grid-cols-2 gap-x-6 gap-y-2 text-sm ${compact ? 'text-xs' : ''}`}>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Montant</span>
            <div className="font-semibold text-zinc-900">
              {formatAmount(paymentCase.amount_cents, paymentCase.currency)}
            </div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Méthode</span>
            <div className="text-zinc-700">{methodLabel}</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Référence</span>
            <div className="font-mono text-xs text-zinc-700">{paymentCase.order_id || '—'}</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Payé par</span>
            <div className="text-zinc-700">{paymentCase.payer_name || '—'}</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">
              {paymentCase.status === PAYMENT_STATUS.PAID ? 'Date paiement' : 'Date création'}
            </span>
            <div className="text-zinc-700">
              {formatDate(
                paymentCase.status === PAYMENT_STATUS.PAID
                  ? paymentCase.updated_at
                  : paymentCase.created_at
              )}
            </div>
          </div>

          {/* Transaction Sogecommerce (CB payée uniquement) — span 2 colonnes */}
          {paidAttempt?.provider_transaction_id && (
            <div className="col-span-2">
              <span className="text-xs uppercase tracking-wider text-zinc-400">
                Transaction Sogecommerce
              </span>
              <div className="font-mono text-xs text-zinc-700">
                {paidAttempt.provider_transaction_id}
              </div>
            </div>
          )}
        </div>

        {/* BED : reste à payer + détail des fractions CB (multi-CB) */}
        {isBed && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-zinc-400">Reste à payer</span>
              <span className="text-sm font-semibold text-indigo-700">
                {formatAmount(remainingCents, paymentCase.currency)}
              </span>
            </div>
            {(overpaidCents > 0 || refunds.length > 0) && (
              <div
                className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
                data-testid="bed-overpayment"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wider text-amber-800">
                    Trop-perçu
                  </span>
                  <span className="text-sm font-semibold text-amber-800">
                    {formatAmount(overpaidCents, paymentCase.currency)}
                  </span>
                </div>
                {refunds.length > 0 && (
                  <ul className="mt-1 flex flex-col gap-0.5">
                    {refunds.map((refund) => (
                      <li
                        key={refund.id}
                        className="flex items-center justify-between gap-2 text-xs text-amber-900"
                        title={refund.comment}
                      >
                        <span>Remboursé le {formatDayOnly(refund.refunded_on)}</span>
                        <span>{formatAmount(refund.amount_cents, paymentCase.currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1 text-xs text-amber-800">
                  {toRefundCents > 0
                    ? `Reste à rembourser : ${formatAmount(toRefundCents, paymentCase.currency)} (remboursement à faire à la main, ${canRecordRefund ? 'puis à noter ici' : 'à noter une fois le dossier soldé'}).`
                    : 'Trop-perçu entièrement remboursé.'}
                </p>
              </div>
            )}
            {subOrders.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1">
                {subOrders.map((attempt) => (
                  <li
                    key={attempt.id}
                    className="flex items-center justify-between gap-2 text-xs text-zinc-600"
                  >
                    <span className="font-mono">
                      {attempt.provider_order_id ||
                        `${PAYMENT_METHOD_LABELS[attempt.method]} du ${formatDate(attempt.created_at)}`}
                    </span>
                    <span className="flex items-center gap-2">
                      <span>{formatAmount(attempt.amount_cents, paymentCase.currency)}</span>
                      <Badge
                        className={
                          attempt.succeeded
                            ? 'border border-green-200 bg-green-50 text-green-700'
                            : 'border border-amber-200 bg-amber-50 text-amber-700'
                        }
                      >
                        {attempt.succeeded ? 'Encaissé' : 'En attente'}
                      </Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Lien de paiement actif : ré-affichage + copie après fermeture
                du dialog de génération. */}
            {activeBedToken && activeBedLink && (
              <div className="mt-3">
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  Lien de paiement (valable jusqu'au {formatDate(activeBedToken.expires_at)})
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <div
                    className="min-w-0 flex-1 truncate rounded-md border border-zinc-200 bg-white px-2 py-1.5 font-mono text-xs text-zinc-600"
                    title={activeBedLink}
                  >
                    {activeBedLink}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyBedLink}
                    className="shrink-0 border-teal-300 text-teal-700 hover:bg-teal-50"
                  >
                    <Copy className="mr-1.5 size-3.5" />
                    Copier
                  </Button>
                </div>
              </div>
            )}

            {/* TICKET-BED-15 : lien expiré/révoqué -> réémission (la RPC
                réutilise le dossier et révoque les anciens tokens). */}
            {canRegenerateBedLink && (
              <div className="mt-3">
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  Lien de paiement expiré ou révoqué
                </span>
                <div className="mt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRegenerateBedLink}
                    disabled={regenerating}
                    className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  >
                    <RefreshCw className="mr-1.5 size-3.5" />
                    {regenerating ? 'Régénération…' : 'Régénérer le lien'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bouton confirmation manuelle */}
        {canConfirm && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <Button
              size="sm"
              onClick={() => setConfirmDialogOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {canConfirmManualCard
                ? 'Confirmer CB ancienne plateforme'
                : paymentCase.status === PAYMENT_STATUS.PARTIAL
                  ? 'Enregistrer un virement ou chèque'
                  : isBed && remainingCents === 0
                    ? 'Clore le dossier (rien à encaisser)'
                    : `Confirmer réception ${confirmMethodLabel}`}
            </Button>
          </div>
        )}

        {/* 00295 : trop-perçu à rembourser -> noter le remboursement fait à la
            main (plafond et rôle contrôlés côté serveur). */}
        {canRecordRefund && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRefundDialogOpen(true)}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              Noter un remboursement
            </Button>
          </div>
        )}

        {/* Bouton ré-ouverture d'un dossier expiré (admin/superadmin) */}
        {canRestore && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRestoreDialogOpen(true)}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              Ré-ouvrir le dossier
            </Button>
          </div>
        )}
      </div>

      {canConfirm && (
        <ConfirmPaymentDialog
          open={confirmDialogOpen}
          onOpenChange={setConfirmDialogOpen}
          paymentCase={paymentCase}
          prospectName={contactName}
          productName={paymentCase.product_name || 'Produit inconnu'}
          onConfirmed={onConfirmed}
        />
      )}

      {canRecordRefund && (
        <RecordRefundDialog
          open={refundDialogOpen}
          onOpenChange={setRefundDialogOpen}
          paymentCase={paymentCase}
          prospectName={contactName}
          productName={paymentCase.product_name || 'Produit inconnu'}
          toRefundCents={toRefundCents}
          onRecorded={onRefundRecorded}
        />
      )}

      {canRestore && (
        <RestoreExpiredDialog
          open={restoreDialogOpen}
          onOpenChange={setRestoreDialogOpen}
          paymentCase={paymentCase}
          prospectName={contactName}
          productName={paymentCase.product_name || 'Produit inconnu'}
          onRestored={onConfirmed}
        />
      )}
    </>
  );
}

export function PaymentSection(props: PaymentSectionProps) {
  const { contactName, compact, onPaymentConfirmed } = props;
  const [bedDialogOpen, setBedDialogOpen] = useState(false);
  const [bedDialogSource, setBedDialogSource] = useState<BedDialogSource | null>(null);
  const [resendBpiDialogOpen, setResendBpiDialogOpen] = useState(false);
  const [manualBpiDialogOpen, setManualBpiDialogOpen] = useState(false);
  const [revokingManualBpi, setRevokingManualBpi] = useState(false);
  const { canManageAssignments } = usePermissions();
  const { revokeBpiManualValidation } = useBpiManualValidationActions();

  const crewId = 'crewId' in props ? props.crewId : undefined;
  const contactId = 'contactId' in props ? props.contactId : undefined;
  const crewMembers = 'crewId' in props ? props.crewMembers : undefined;

  // (00242) Ids des membres, pour élargir la lecture des attestations au pont
  // `crew_members` — miroir exact de ce que le serveur voit.
  const memberContactIds = useMemo(() => (crewMembers ?? []).map((m) => m.id), [crewMembers]);

  // Branchement conditionnel : hook par contact OU par crew selon la prop.
  // L'autre hook recoit `undefined` et retourne une liste vide sans requete.
  const byContact = usePaymentCase(contactId);
  const byCrew = usePaymentCasesByCrew(crewId);
  const { paymentCases, loading, refresh } = crewId ? byCrew : byContact;

  // Pour un porteur contact, l'édition doit être levée d'ambiguïté côté client :
  // on ne la retient que si tous ses dossiers BPI portent sur le même produit.
  // Sert au bouton de validation manuelle ET au filtre d'édition ci-dessous.
  const contactBpiProductId = useMemo(() => {
    if (crewId) return undefined;
    const products = new Set(
      paymentCases.filter((pc) => pc.scope === PAYMENT_SCOPE.BPI).map((pc) => pc.product_id)
    );
    return products.size === 1 ? [...products][0] : undefined;
  }, [crewId, paymentCases]);

  // (00242) ÉDITION de lecture des attestations : celle de l'équipage
  // (`crews.product_id`, passé en prop) ou, pour un contact, son unique édition
  // BPI. `undefined` -> pas de filtre, comportement historique (un contact
  // multi-éditions n'a pas d'édition canonique à opposer au serveur).
  const manualScopeProductId =
    ('crewId' in props ? (props.productId ?? undefined) : undefined) ?? contactBpiProductId;

  // T1 : attestations « BPI réglé » posées à la main par le staff (dossiers
  // sans paiement BPI dans le système : ancienne plateforme, invité exonéré,
  // report d'édition).
  const { validations: manualValidations, refresh: refreshManualValidations } =
    useBpiManualValidations({
      crewId,
      contactId,
      memberContactIds,
      productId: manualScopeProductId,
    });

  // Émission « Lien BED » : la contrainte DB autorise un seul BED par
  // (porteur, produit), quel que soit son statut. La RPC refuse aussi de
  // réémettre un BED terminal. On cherche donc un BPI payé dont le produit
  // n'a encore AUCUN BED, au lieu de bloquer globalement sur un BED d'un autre
  // produit ou de proposer une action impossible après un BED terminal.
  const productsWithBed = useMemo(
    () =>
      new Set(
        paymentCases.filter((pc) => pc.scope === PAYMENT_SCOPE.BED).map((pc) => pc.product_id)
      ),
    [paymentCases]
  );

  const paidBpiCase = useMemo(
    () =>
      paymentCases.find(
        (pc) =>
          pc.scope === PAYMENT_SCOPE.BPI &&
          pc.status === PAYMENT_STATUS.PAID &&
          !productsWithBed.has(pc.product_id)
      ),
    [paymentCases, productsWithBed]
  );

  // Attestation manuelle en place. Une seule par (produit, porteur) côté DB ;
  // la liste est déjà triée par validated_at DESC.
  // (00242) La liste peut désormais contenir des attestations posées sur des
  // MEMBRES de l'équipage. On rejoue la précédence SERVEUR
  // (`resolve_bpi_manual_proof`) : celle posée sur l'ÉQUIPAGE prime.
  const manualValidation = useMemo(() => {
    if (manualValidations.length === 0) return null;
    if (crewId) {
      return manualValidations.find((v) => v.crew_id === crewId) ?? manualValidations[0];
    }
    return manualValidations[0];
  }, [manualValidations, crewId]);

  // (00242) Provenance : l'attestation retenue a-t-elle été posée sur un MEMBRE
  // plutôt que sur l'équipage ? Le staff doit le voir, sinon il en repose une
  // seconde sur l'équipage en croyant qu'il n'y en a aucune.
  const manualValidationMember = useMemo(() => {
    if (!crewId || !manualValidation?.contact_id) return null;
    return (crewMembers ?? []).find((m) => m.id === manualValidation.contact_id) ?? null;
  }, [crewId, manualValidation, crewMembers]);

  // Le BED est débloqué par un BPI réellement payé OU par une validation
  // manuelle, sur un produit qui n'a pas déjà son dossier BED.
  const manualBedSource =
    !paidBpiCase && manualValidation && !productsWithBed.has(manualValidation.product_id)
      ? manualValidation
      : null;
  const canGenerateBedLink = !!paidBpiCase || !!manualBedSource;

  // Validation manuelle : réservée admin/superadmin (la RPC re-vérifie le rôle
  // côté serveur) et proposée seulement tant qu'aucun BPI n'est réellement payé.
  // (`contactBpiProductId` est défini plus haut : il sert aussi à borner la
  // lecture des attestations à la bonne édition.)
  // (00307, TICKET-BED-27) Sur l'édition retenue seulement, comme le serveur
  // (bpi_paid_owner_conflict est par édition) : un équipage reporté dont le BPI
  // payé est sur l'ancienne édition doit pouvoir rattacher son acompte à la
  // sienne, sinon le lien BED reste refusé (bed_bpi_paid_on_other_edition).
  const hasPaidBpi = paymentCases.some(
    (pc) =>
      pc.scope === PAYMENT_SCOPE.BPI &&
      pc.status === PAYMENT_STATUS.PAID &&
      (!manualScopeProductId || pc.product_id === manualScopeProductId)
  );
  const canValidateBpiManually =
    canManageAssignments() && !hasPaidBpi && (!!crewId || !!contactBpiProductId);

  // Renvoi du mail de paiement BPI (TICKET-CRM-10) : dossier BPI en attente
  // (non payé, non terminal). Le mail est renvoyé par la route interne 4LT via
  // l'Edge Function send-bpi-link (template choisi selon le statut du case).
  // Comme paidBpiCase plus haut, .find() prend le plus récent si plusieurs
  // cases BPI coexistent (multi-produits, rarissime) — comportement assumé.
  const resendableBpiCase = useMemo(
    () =>
      paymentCases.find(
        (pc) => pc.scope === PAYMENT_SCOPE.BPI && RESENDABLE_BPI_STATUSES.has(pc.status)
      ),
    [paymentCases]
  );

  // Origine du dossier BED à ouvrir : un BPI réellement payé (chemin
  // historique) ou une validation manuelle (T1), auquel cas il n'y a AUCUN
  // dossier BPI payé à présenter au dialog.
  const currentBedSource = useMemo<BedDialogSource | null>(() => {
    if (paidBpiCase) {
      return {
        paidBpiCaseId: paidBpiCase.id,
        productId: paidBpiCase.product_id,
        productName: paidBpiCase.product_name || 'Produit inconnu',
      };
    }
    if (manualBedSource) {
      return {
        paidBpiCaseId: null,
        productId: manualBedSource.product_id,
        productName:
          paymentCases.find((pc) => pc.product_id === manualBedSource.product_id)?.product_name ||
          'Produit inconnu',
      };
    }
    return null;
  }, [paidBpiCase, manualBedSource, paymentCases]);

  async function handleConfirmed() {
    await refresh();
    onPaymentConfirmed?.();
  }

  // Appelé par BedLinkDialog à sa FERMETURE (pas à la génération) : le refresh
  // fait apparaître le BED draft -> canGenerateBedLink passe false -> le bouton
  // disparaît. Le dialog lui-même reste monté tant qu'il est ouvert (cf. la
  // condition de montage `canGenerateBedLink || bedDialogOpen` plus bas).
  async function handleBedGenerated() {
    await refresh();
  }

  // T1 : la validation manuelle change l'acompte ET débloque le lien BED — on
  // rafraîchit les deux sources.
  async function handleManualBpiValidated() {
    await Promise.all([refreshManualValidations(), refresh()]);
  }

  // Retrait d'une attestation posée par erreur. Le serveur refuse si le solde
  // BED a déjà été encaissé sur la base de cette déduction.
  async function handleRevokeManualBpi() {
    if (!manualValidation) return;
    setRevokingManualBpi(true);
    try {
      // (00242) On vise le PORTEUR DE LA LIGNE, pas le porteur de la fiche :
      // l'attestation affichée peut avoir été posée sur un membre de
      // l'équipage. Passer crewId ici lèverait bpi_manual_validation_not_found.
      const result = await revokeBpiManualValidation({
        crewId: manualValidation.crew_id ?? undefined,
        contactId: manualValidation.contact_id ?? undefined,
        productId: manualValidation.product_id,
      });
      if (!result.success) {
        toast.error(result.error ?? 'Erreur lors du retrait de la validation manuelle.');
        return;
      }
      toast.success('Validation manuelle retirée.');
      await handleManualBpiValidated();
    } finally {
      setRevokingManualBpi(false);
    }
  }

  function handleOpenBedDialog() {
    if (!currentBedSource) return;

    setBedDialogSource(currentBedSource);
    setBedDialogOpen(true);
  }

  function handleBedDialogOpenChange(open: boolean) {
    setBedDialogOpen(open);
    if (!open) {
      setBedDialogSource(null);
    }
  }

  const activeBedDialogSource = bedDialogSource ?? currentBedSource;

  if (loading) {
    return (
      <Card className="p-6">
        <h2 className={`font-semibold text-zinc-900 ${compact ? 'text-sm' : 'text-xl'}`}>
          Paiement
        </h2>
        <p className="mt-2 text-sm text-zinc-400">Chargement...</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <CreditCard className="size-5 text-zinc-400" />
        <h2 className={`font-semibold text-zinc-900 ${compact ? 'text-sm' : 'text-xl'}`}>
          Paiement
        </h2>
      </div>

      {paymentCases.length === 0 ? (
        <p className="text-center text-sm text-zinc-400 py-4">
          {'crewId' in props
            ? 'Aucun paiement enregistré pour cet équipage.'
            : 'Aucun paiement enregistré pour ce contact.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {paymentCases.map((pc) => (
            <PaymentCaseCard
              key={pc.id}
              paymentCase={pc}
              contactName={contactName}
              compact={compact}
              onConfirmed={handleConfirmed}
              onBedLinkRegenerated={refresh}
              onRefundRecorded={refresh}
            />
          ))}
        </div>
      )}

      {/* T1 : attestation « BPI réglé » posée à la main. Aucun encaissement
          n'existe — on l'affiche à part des dossiers de paiement pour ne pas
          laisser croire à un paiement enregistré. */}
      {manualValidation && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
          <div className="mb-3 flex items-center gap-2">
            <BadgeCheck className="size-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-900">BPI validé manuellement</span>
          </div>
          <div className={`grid grid-cols-2 gap-x-6 gap-y-2 text-sm ${compact ? 'text-xs' : ''}`}>
            <div>
              <span className="text-xs uppercase tracking-wider text-emerald-700/70">
                Montant déduit du BED
              </span>
              <div className="font-semibold text-zinc-900">
                {formatAmount(manualValidation.amount_cents, 'EUR')}
              </div>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-emerald-700/70">
                Validé le
              </span>
              <div className="text-zinc-700">{formatDate(manualValidation.validated_at)}</div>
            </div>
            {manualValidation.note && (
              <div className="col-span-2">
                <span className="text-xs uppercase tracking-wider text-emerald-700/70">Motif</span>
                <div className="text-zinc-700">{manualValidation.note}</div>
              </div>
            )}
          </div>
          {/* (00242) Provenance : l'attestation peut avoir été posée sur un
              MEMBRE (le pilote, avant même la création de l'équipage). Elle
              débloque bien l'équipage côté serveur — il faut le dire, sinon le
              staff en repose une seconde. */}
          {manualValidationMember && (
            <p className="mt-3 text-xs text-emerald-800">
              Posée sur{' '}
              <strong>
                {[manualValidationMember.first_name, manualValidationMember.last_name]
                  .filter(Boolean)
                  .join(' ') || 'un membre'}
              </strong>
              , membre de l&apos;équipage.
            </p>
          )}
          <p className="mt-3 text-xs text-emerald-800">
            Aucun encaissement n&apos;est enregistré dans le CRM : ce montant est uniquement déduit
            du solde BED.
          </p>
          {canManageAssignments() && (
            <div className="mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRevokeManualBpi}
                disabled={revokingManualBpi}
                className="border-zinc-300 text-zinc-600 hover:bg-zinc-100"
              >
                <Undo2 className="mr-1.5 size-3.5" />
                {revokingManualBpi ? 'Retrait…' : 'Retirer la validation'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Actions cross-case : validation manuelle du BPI (T1), émission d'un
          lien BED (solde restant dû) et/ou renvoi du mail de paiement BPI
          (dossier en attente, TICKET-CRM-10) */}
      {(canGenerateBedLink || resendableBpiCase || canValidateBpiManually) && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-200 pt-4">
          {canGenerateBedLink && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenBedDialog}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              <LinkIcon className="mr-2 size-4" />
              Générer un lien BED
            </Button>
          )}
          {canValidateBpiManually && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setManualBpiDialogOpen(true)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <BadgeCheck className="mr-2 size-4" />
              {manualValidation ? 'Modifier la validation du BPI' : 'Valider le BPI manuellement'}
            </Button>
          )}
          {resendableBpiCase && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setResendBpiDialogOpen(true)}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              <Mail className="mr-2 size-4" />
              Renvoyer le mail de paiement
            </Button>
          )}
        </div>
      )}

      {/* Le dialog reste monté tant qu'il est OUVERT, même si le refresh rend
          canGenerateBedLink false (le BED draft créé pendant la session ne doit
          pas démonter l'écran lien avant copie/envoi). Le bouton ci-dessus, lui,
          reste gaté par canGenerateBedLink. */}
      {(canGenerateBedLink || bedDialogOpen) && activeBedDialogSource && (
        <BedLinkDialog
          open={bedDialogOpen}
          onOpenChange={handleBedDialogOpenChange}
          crewId={crewId}
          contactId={contactId}
          paidBpiCaseId={activeBedDialogSource.paidBpiCaseId}
          productId={activeBedDialogSource.productId}
          prospectName={contactName}
          productName={activeBedDialogSource.productName}
          onGenerated={handleBedGenerated}
        />
      )}

      {/* T1 : validation manuelle du BPI, réservée admin/superadmin. Reste
          monté tant que l'action est proposée (elle ne disparaît pas après une
          validation : le staff peut corriger le montant). */}
      {canValidateBpiManually && (
        <ManualBpiValidationDialog
          open={manualBpiDialogOpen}
          onOpenChange={setManualBpiDialogOpen}
          crewId={crewId}
          contactId={contactId}
          productId={crewId ? undefined : contactBpiProductId}
          prospectName={contactName}
          existingValidation={manualValidation}
          onValidated={handleManualBpiValidated}
        />
      )}

      {/* Même pattern de montage que BedLinkDialog : le dialog reste monté tant
          que le dossier est renvoyable (l'envoi du mail ne change pas le statut
          du case, donc pas de démontage en cours de session). */}
      {resendableBpiCase && (
        <ResendBpiLinkDialog
          open={resendBpiDialogOpen}
          onOpenChange={setResendBpiDialogOpen}
          paymentCaseId={resendableBpiCase.id}
          payerContactId={resendableBpiCase.payer_contact_id}
          prospectName={contactName}
          productName={resendableBpiCase.product_name || 'Produit inconnu'}
        />
      )}
    </Card>
  );
}
